import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AppShell } from "./components/AppShell";
import { PreferencesProvider } from "./components/PreferencesProvider";
import { RequireAuth } from "./components/RequireAuth";
import { RequireSuperAdmin } from "./components/RequireSuperAdmin";
import { PartnerScopeProvider } from "./components/PartnerScopeProvider";
import "./index.css";
import { AiCostsPage } from "./pages/AiCosts";
import { CostsPage } from "./pages/Costs";
import { ApiLogsPage } from "./pages/ApiLogs";
import { AuditLogPage } from "./pages/AuditLog";
import { Dashboard } from "./pages/Dashboard";
import { HealthPage } from "./pages/Health";
import { ImpersonatePage } from "./pages/Impersonate";
import { IntegrationHealthPage } from "./pages/IntegrationHealth";
import { LocationDetailPage } from "./pages/LocationDetail";
import { LocationsPage } from "./pages/Locations";
import { LoginPage } from "./pages/Login";
import { OnboardPage } from "./pages/Onboard";
import { OrganizationDetailPage } from "./pages/OrganizationDetail";
import { OrganizationsPage } from "./pages/Organizations";
import { PartnerDetailPage } from "./pages/PartnerDetail";
import { FeatureDetailPage } from "./pages/FeatureDetail";
import { PlatformCatalogPage } from "./pages/PlatformCatalog";
import { ProfilePage } from "./pages/Profile";
import { ProviderCredentialsPage } from "./pages/ProviderCredentials";
import { PartnersPage } from "./pages/Partners";
import { PeoplePage } from "./pages/People";
import { PersonDetailPage } from "./pages/PersonDetail";
import { PermissionsCatalogPage } from "./pages/PermissionsCatalog";
import { RoleDetailPage } from "./pages/RoleDetail";
import { RoleNewPage } from "./pages/RoleNew";
import { RolesPage } from "./pages/Roles";
import { UsersPage } from "./pages/Users";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error("#root element missing from index.html");
}

createRoot(rootEl).render(
  <StrictMode>
    <PreferencesProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/health" element={<HealthPage />} />
            <Route
              element={
                <RequireAuth>
                  <PartnerScopeProvider>
                    <AppShell />
                  </PartnerScopeProvider>
                </RequireAuth>
              }
            >
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route
                path="/onboard"
                element={
                  <RequireSuperAdmin>
                    <OnboardPage />
                  </RequireSuperAdmin>
                }
              />
              {/* Partners list + drill-down (per 2026-04-21 tenancy lock). */}
              <Route path="/partners" element={<PartnersPage />} />
              <Route path="/partners/:id" element={<PartnerDetailPage />} />
              <Route path="/organizations" element={<OrganizationsPage />} />
              <Route path="/organizations/:id" element={<OrganizationDetailPage />} />
              <Route path="/locations" element={<LocationsPage />} />
              <Route path="/locations/:id" element={<LocationDetailPage />} />
              <Route path="/provider-credentials" element={<ProviderCredentialsPage />} />
              <Route path="/people" element={<PeoplePage />} />
              <Route path="/people/:pool/:id" element={<PersonDetailPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route
                path="/impersonate"
                element={
                  <RequireSuperAdmin>
                    <ImpersonatePage />
                  </RequireSuperAdmin>
                }
              />
              <Route path="/audit" element={<AuditLogPage />} />
              <Route
                path="/platform/costs"
                element={
                  <RequireSuperAdmin>
                    <CostsPage />
                  </RequireSuperAdmin>
                }
              />
              <Route
                path="/platform/ai-costs"
                element={
                  <RequireSuperAdmin>
                    <AiCostsPage />
                  </RequireSuperAdmin>
                }
              />
              {/* Integration Health (V0072). Server-side gate denies
                  partner_admins without a delegation row; the page
                  itself shows the gate copy on a 403. Sidebar nav
                  hides the link for non-super non-delegated users via
                  the same probe. */}
              <Route path="/integrations/health" element={<IntegrationHealthPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route
                path="/platform/catalog"
                element={
                  <RequireSuperAdmin>
                    <PlatformCatalogPage />
                  </RequireSuperAdmin>
                }
              />
              <Route
                path="/platform/catalog/features/:path"
                element={
                  <RequireSuperAdmin>
                    <FeatureDetailPage />
                  </RequireSuperAdmin>
                }
              />
              {/* Roles + permissions catalog. Page-level gate is open
                  to any authenticated admin; the API enforces real
                  visibility (super_admin sees all, partner_admin sees
                  system + their subtree). The /new path lands on the
                  create-role editor; /:id lands on detail with inline
                  edit + delete (custom roles only). */}
              <Route path="/platform/roles" element={<RolesPage />} />
              <Route path="/platform/roles/new" element={<RoleNewPage />} />
              <Route path="/platform/roles/:id" element={<RoleDetailPage />} />
              <Route
                path="/platform/permissions"
                element={
                  <RequireSuperAdmin>
                    <PermissionsCatalogPage />
                  </RequireSuperAdmin>
                }
              />
              <Route
                path="/system/api-logs"
                element={
                  <RequireSuperAdmin>
                    <ApiLogsPage />
                  </RequireSuperAdmin>
                }
              />
            </Route>
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </PreferencesProvider>
  </StrictMode>,
);
